import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../contexts/AuthContext';
import { occurrenceService, resolutionService, categoryService } from '../services/occurrence.service';
import { Occurrence, Resolution, STATUS_LABELS, OccurrenceStatus, StatusHistory, PRIORITY_LABELS, PRIORITY_COLORS, OccurrencePriority, Category } from '../types';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

function ImageGallery({ images, index, onClose }: { images: string[]; index: number; onClose: () => void }) {
  const [current, setCurrent] = useState(index);

  const prev = useCallback(() => setCurrent((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setCurrent((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  const touchRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    let sx = 0;
    const onStart = (e: TouchEvent) => { sx = e.touches[0].clientX; };
    const onEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - sx;
      if (Math.abs(dx) > 50) {
        dx > 0 ? prev() : next();
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [prev, next]);

  return (
    <div ref={touchRef} className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl z-10"
      >
        ✕
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl z-10 px-2"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white text-4xl z-10 px-2"
          >
            ›
          </button>
        </>
      )}

      <img
        src={images[current]}
        alt=""
        className="max-w-full max-h-full object-contain p-10"
        onClick={(e) => e.stopPropagation()}
      />

      {images.length > 1 && (
        <div className="absolute bottom-4 text-white/60 text-sm">
          {current + 1} / {images.length}
        </div>
      )}
    </div>
  );
}

export default function OccurrenceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [occurrence, setOccurrence] = useState<Occurrence | null>(null);
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState('');
  const [newPriority, setNewPriority] = useState<OccurrencePriority>(OccurrencePriority.MEDIUM);
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryId, setNewCategoryId] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolveData, setResolveData] = useState({ notes: '', lat: 0, lng: 0 });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    loadDetail();
  }, [id]);

  const loadDetail = async () => {
    if (!id) return;
    try {
      const [occ, history] = await Promise.all([
        occurrenceService.findOne(id),
        occurrenceService.getStatusHistory(id).catch(() => []),
      ]);
      setOccurrence(occ);
      setStatusHistory(history);
      setNewStatus(occ.status);
      setNewPriority(occ.priority || OccurrencePriority.MEDIUM);
      setNewCategoryId(occ.categoryId);
      
      try {
        const cats = await categoryService.findByCity(occ.cityId || user?.cityId || '');
        setCategories(cats.filter(c => c.isActive || c.id === occ.categoryId));
      } catch {
        // Categories not available
      }

      try {
        const res = await resolutionService.findByOccurrence(id);
        setResolution(res);
      } catch {
        // No resolution yet
      }
    } catch (err) {
      console.error('Failed to load occurrence', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!id || !newStatus) return;
    try {
      if (newCategoryId !== occurrence?.categoryId) {
        await occurrenceService.updateCategory(id, newCategoryId);
      }
      await occurrenceService.updatePriority(id, newPriority);
      await occurrenceService.updateStatus(id, newStatus);
      if (newStatus === OccurrenceStatus.RESOLVED) {
        setShowResolveForm(true);
      } else {
        navigate('/occurrences');
      }
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleResolve = async () => {
    if (!id) return;
    if (!resolveData.notes.trim()) {
      alert('Preencha as observações técnicas');
      return;
    }
    try {
      await resolutionService.create({
        occurrenceId: id,
        notes: resolveData.notes,
        lat: Number(occurrence?.lat) || 0,
        lng: Number(occurrence?.lng) || 0,
        resolutionImages: [],
      });
      navigate('/occurrences');
    } catch (err: any) {
      const msg = err?.response?.data?.message?.[0] || err?.response?.data?.message || err?.message;
      alert(msg || 'Erro ao registrar resolução');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'in_progress': return 'status-in-progress';
      case 'resolved': return 'status-resolved';
      default: return 'status-cancelled';
    }
  };

  function elapsed(from: string, to: string): string {
    const diff = new Date(to).getTime() - new Date(from).getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}min`;
  }

  if (loading) return <div className="p-8">Carregando...</div>;
  if (!occurrence) return <div className="p-8">Ocorrência não encontrada</div>;

  const isManager = user?.userType === 'admin' || user?.userType === 'manager';

  return (
    <>
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-semibold">{occurrence.category?.name || occurrence.categoryId}</h2>
            </div>
            <div className="flex items-center gap-2">
              {(user?.userType !== 'citizen' || occurrence.status !== OccurrenceStatus.PENDING) && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: PRIORITY_COLORS[occurrence.priority] + '20', color: PRIORITY_COLORS[occurrence.priority], border: `1px solid ${PRIORITY_COLORS[occurrence.priority]}` }}
                >
                  {PRIORITY_LABELS[occurrence.priority]}
                </span>
              )}
              <span className={getStatusColor(occurrence.status)}>
                {STATUS_LABELS[occurrence.status]}
              </span>
            </div>
          </div>
          <p className="text-gray-700 mb-4">{occurrence.description}</p>
          {user?.userType !== 'citizen' && occurrence.citizen && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm text-gray-700">
              <p><span className="font-medium">Solicitante:</span> {occurrence.citizen.name}</p>
              <p><span className="font-medium">Email:</span> {occurrence.citizen.email}</p>
              {occurrence.citizen.phone && (
                <p><span className="font-medium">Telefone:</span> {occurrence.citizen.phone}</p>
              )}
            </div>
          )}
          {occurrence.images?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Fotos ({occurrence.images.length})</h4>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {occurrence.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => { setGalleryIndex(i); setGalleryOpen(true); }}
                    className="aspect-square rounded-lg overflow-hidden border hover:opacity-90 transition-opacity"
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {galleryOpen && occurrence.images && (
            <ImageGallery images={occurrence.images} index={galleryIndex} onClose={() => setGalleryOpen(false)} />
          )}
          <div className="text-sm text-gray-500">
            <p>Criado em: {new Date(occurrence.createdAt).toLocaleString('pt-BR')}</p>
            {occurrence.resolvedAt && (
              <p>Resolvido em: {new Date(occurrence.resolvedAt).toLocaleString('pt-BR')}</p>
            )}
            {occurrence.address && <p className="mt-1">{occurrence.address}</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-2">Localização</h3>
          <div className="h-64 rounded-lg overflow-hidden relative z-0">
            <MapContainer center={[occurrence.lat, occurrence.lng]} zoom={15} className="h-full w-full" dragging={false} zoomControl={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <Marker position={[occurrence.lat, occurrence.lng]}>
                <Popup>{occurrence.category?.name || occurrence.categoryId}</Popup>
              </Marker>
            </MapContainer>
          </div>
        </div>

        {statusHistory.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Histórico de Status</h3>
            <div className="space-y-3">
              {statusHistory.map((h, i) => {
                const prev = statusHistory[i - 1];
                return (
                  <div key={h.id} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(h.status)}`} />
                      {i < statusHistory.length - 1 && <div className="w-0.5 h-8 bg-gray-300" />}
                    </div>
                    <div className="text-sm">
                      {h.description ? (
                        <p>
                          <span className="text-gray-700">{h.description}</span>
                          <span className="text-gray-500 ml-2">
                            {new Date(h.changedAt).toLocaleString('pt-BR')}
                          </span>
                        </p>
                      ) : (
                      <p>
                        <span className={getStatusColor(h.status)}>
                          {STATUS_LABELS[h.status]}
                        </span>
                        <span className="text-gray-500 ml-2">
                          {new Date(h.changedAt).toLocaleString('pt-BR')}
                        </span>
                      </p>
                      )}
                      {prev && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Tempo desde o status anterior: {elapsed(prev.changedAt, h.changedAt)}
                        </p>
                      )}
                      {i === 0 && (
                        <p className="text-xs text-gray-400 mt-0.5">Cadastro inicial da ocorrência</p>
                      )}
                    </div>
                  </div>
                );
              })}
              {occurrence.resolvedAt && (
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                  </div>
                  <div className="text-sm">
                    <p className="text-gray-700 font-medium">
                      Tempo total para conclusão: {elapsed(occurrence.createdAt, occurrence.resolvedAt)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {isManager && occurrence.status !== OccurrenceStatus.RESOLVED && occurrence.status !== OccurrenceStatus.CANCELLED && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Gerenciar Ocorrência</h3>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                <select
                  value={newPriority}
                  onChange={(e) => setNewPriority(e.target.value as OccurrencePriority)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {(Object.entries(PRIORITY_LABELS) as [OccurrencePriority, string][]).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                <select
                  value={newCategoryId}
                  onChange={(e) => setNewCategoryId(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <button onClick={handleStatusUpdate} className="btn-primary">
                Atualizar
              </button>
            </div>
          </div>
        )}

        {showResolveForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Registrar Resolução</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações Técnicas</label>
                <textarea
                  value={resolveData.notes}
                  onChange={(e) => setResolveData({ ...resolveData, notes: e.target.value })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  rows={3}
                  required
                />
              </div>
              <button onClick={handleResolve} className="btn-primary">
                Registrar Resolução
              </button>
            </div>
          </div>
        )}

        {resolution && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Resolução</h3>
            <p className="text-gray-700 mb-2">{resolution.notes}</p>
            <div className="text-sm text-gray-500">
              <p>Resolvido em: {new Date(resolution.createdAt).toLocaleString('pt-BR')}</p>
            </div>
          </div>
                )}
    </>
  );
}

